import { FeeScheduleCategoryType } from "_shared/fee-schedules/categories/types/fee-schedule-category-type";
import { UnavailableIdJSON, UnavailableReason } from "_shared/_api/by-ids/by-ids-result";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import { FeeScheduleCategoryMaskedCurrency, Masked, MaskedFeeSchedule, MaskedTeam } from "work/masked-fields";
import { ProposalAction } from "work/proposals/__constants/proposal-lifecycle";
import ProposalHiredTerms from "work/proposals/hired-terms/proposal-hired-terms";
import Proposal from "work/proposals/proposal";
import Guid from "_shared/guids/guid";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

/** Where the comparison of one request group lives. Shared by the page's route and every link to it. */
export function proposalComparisonPath(rfpId: string): string {
  return `/proposals/compare/${rfpId}`;
}

/**
 * The vendor team and fee schedule one column shows, and where they come from (#6633).
 *
 * - `hired`: as the proposal was hired, kept by the server at the hire.
 * - `live`: as the proposal stands. For a proposal never hired that is its decision.
 * - `liveAfterHire`: a proposal hired before hired terms were kept. Only today's team and fees
 *   exist, and the page says so rather than presenting them as what was hired.
 */
export type ComparedTerms = {
  source: "hired" | "live" | "liveAfterHire";
  hiredAt?: AHDate;
  vendorTeam: VendorTeam | MaskedTeam | null;
  feeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule;
};

/** One firm's proposal in the comparison, or one the reader may not read. */
export type ProposalComparisonColumn =
  | { kind: "proposal"; id: string; proposal: Proposal; terms: ComparedTerms }
  | { kind: "unavailable"; id: string; reason: UnavailableReason };

/** One rate a firm quotes for a category, as the server sent it for this reader. */
export type ComparedRate =
  | { kind: "fee"; fee: Fee }
  /** The category is there and its fee is not set yet. */
  | { kind: "deferred" }
  /** The server withheld this category's rate, unit and currency from this reader. */
  | { kind: "currencyMasked" };

/** Who bills a category: a named person, nobody, or someone the server withheld from this reader. */
export type ComparedTeamMember = { kind: "member"; member: EntityMember } | { kind: "none" } | { kind: "masked" };

/** Every attribute of one category a firm quotes, so a change in any of them shows. */
export type ComparedFee = {
  name: string;
  rate: ComparedRate;
  teamMember: ComparedTeamMember;
  type: FeeScheduleCategoryType;
};

/** What one firm's column holds on one fee category row. */
export type FeeComparisonCell =
  /** The server withheld this firm's whole fee schedule from this reader. */
  | { kind: "masked" }
  /** The reader may not read this firm's proposal at all. */
  | { kind: "unavailable" }
  /** This firm quotes nothing under the category. */
  | { kind: "absent" }
  | { kind: "fees"; fees: ComparedFee[] };

/** One fee category, with what every firm quotes for it. */
export type FeeComparisonRow = {
  /**
   * The category's names, in column order and without repeats. More than one means the category
   * was renamed between proposals that share it: the earlier names are what it was called.
   */
  names: string[];
  cells: FeeComparisonCell[];
  /** Whether the readable columns quote it differently. A masked or unreadable column is no evidence. */
  differs: boolean;
};

/**
 * The proposals one request produced, side by side, for the client (#581).
 *
 * Built only from what the server sent this reader. A field the server masked for one firm stays
 * masked in that firm's column, and nothing here fills it from another firm or treats it as empty.
 * A proposal the reader may not read is a column of its own, marked unavailable, never dropped.
 *
 * A hired proposal is shown as it was hired, not as its work stands today (#6633).
 *
 * Fees are aligned by category id, which a category keeps when its proposal is copied, so a renamed
 * category stays one row. A category with no id in common is aligned by name, ignoring case and
 * surrounding spaces. Each rate keeps its own currency and unit. Nothing here adds rates up or ranks
 * firms: there is no total-cost field, and rates in different currencies cannot be compared without
 * a conversion the product does not make.
 */
export default class ProposalComparison {
  public static fromResult(params: {
    found: readonly Proposal[];
    unavailable: readonly UnavailableIdJSON[];
    /** The terms each hired proposal was hired on, by proposal id, as the server sent them. */
    hiredTerms?: ReadonlyMap<string, ProposalHiredTerms>;
  }): ProposalComparison {
    return new ProposalComparison([
      ...params.found.map((proposal): ProposalComparisonColumn => {
        const id = proposal.id?.value ?? "";
        return { kind: "proposal", id, proposal, terms: comparedTerms(proposal, params.hiredTerms?.get(id)) };
      }),
      ...params.unavailable.map(
        (entry): ProposalComparisonColumn => ({ kind: "unavailable", id: entry.id, reason: entry.reason }),
      ),
    ]);
  }

  public readonly columns: readonly ProposalComparisonColumn[];

  /** Every category any readable firm quotes, in the order they first appear, with each firm's fees. */
  public get feeRows(): FeeComparisonRow[] {
    const rows: { names: string[]; cells: FeeComparisonCell[] }[] = [];
    const rowsById = new Map<string, number>();
    const rowsByName = new Map<string, number>();
    this.columns.forEach((column, columnIndex) => {
      if (column.kind !== "proposal") return;
      const feeSchedule = column.terms.feeSchedule;
      if (Masked.isMasked(feeSchedule)) return;
      for (const category of feeSchedule) {
        const name = category.name.value.trim();
        const id = category.id.value;
        let rowIndex = rowsById.get(id) ?? rowsByName.get(categoryKey(name));
        if (rowIndex === undefined) {
          rowIndex = rows.length;
          rows.push({ names: [], cells: this.columns.map((other) => emptyCell(other)) });
        }
        rowsById.set(id, rowIndex);
        if (!rowsByName.has(categoryKey(name))) rowsByName.set(categoryKey(name), rowIndex);
        const row = rows[rowIndex];
        if (!row.names.some((known) => categoryKey(known) === categoryKey(name))) row.names.push(name);
        const cell = row.cells[columnIndex];
        const fee = comparedFee(category);
        row.cells[columnIndex] = cell.kind === "fees" ? { kind: "fees", fees: [...cell.fees, fee] } : { kind: "fees", fees: [fee] };
      }
    });
    return rows.map((row) => ({ ...row, differs: feeCellsDiffer(row.cells) }));
  }

  /** The firms whose whole fee schedule the server withheld from this reader. */
  public get hasMaskedFeeSchedule(): boolean {
    return this.columns.some((column) => column.kind === "proposal" && Masked.isMasked(column.terms.feeSchedule));
  }

  /** Whether any column is a proposal that is over: hired, closed, rejected or awarded elsewhere. */
  public get hasArchived(): boolean {
    return this.columns.some((column) => column.kind === "proposal" && isArchivedColumn(column.proposal));
  }

  /** Every currency a visible rate is quoted in, so the page can say why it adds nothing up. */
  public get currencies(): string[] {
    const currencies = new Set<string>();
    for (const row of this.feeRows) {
      for (const cell of row.cells) {
        if (cell.kind !== "fees") continue;
        for (const fee of cell.fees) {
          if (fee.rate.kind === "fee") currencies.add(fee.rate.fee.rate.currency.toString());
        }
      }
    }
    return [...currencies].sort();
  }

  /** Whether any column is a proposal this reader may read, rather than a refusal. */
  public get hasReadableColumn(): boolean {
    return this.columns.some((column) => column.kind === "proposal");
  }

  /** Every person a readable fee names as who bills it, once each, so the page looks them all up at once. */
  public get namedPeople(): Guid[] {
    const people = new Map<string, Guid>();
    for (const row of this.feeRows) {
      for (const cell of row.cells) {
        if (cell.kind !== "fees") continue;
        for (const fee of cell.fees) {
          if (fee.teamMember.kind !== "member") continue;
          people.set(fee.teamMember.member.userId.value, fee.teamMember.member.userId);
        }
      }
    }
    return [...people.values()];
  }

  public get unavailableCount(): number {
    return this.columns.filter((column) => column.kind === "unavailable").length;
  }

  public constructor(columns: readonly ProposalComparisonColumn[]) {
    this.columns = columns;
  }

  /** The same comparison without the proposals that are over. A column the reader may not read stays. */
  public withoutArchived(): ProposalComparison {
    return new ProposalComparison(
      this.columns.filter((column) => column.kind !== "proposal" || !isArchivedColumn(column.proposal)),
    );
  }

  /**
   * Whether the readable columns hold different values of one term. `signatureOf` answers a
   * comparable value for a column, or undefined when the server withheld it from this reader.
   */
  public differs(signatureOf: (proposal: Proposal, terms: ComparedTerms) => string | undefined): boolean {
    return valuesDiffer(
      this.columns.map((column) => (column.kind === "proposal" ? signatureOf(column.proposal, column.terms) : undefined)),
    );
  }
}

/**
 * The actions the server offers `userId` on this proposal, in the lifecycle's order. The server's
 * `availableActions` already names only the reader, so this is a read, not a decision.
 */
export function actionsOfferedTo(proposal: Proposal, userId: Guid | undefined): ProposalAction[] {
  if (!userId) return [];
  return Object.values(ProposalAction).filter((action) =>
    (proposal.availableActions[action] ?? []).some((member) => member.isEqualTo(userId)),
  );
}

/** A vendor team as a value two columns can be compared by: its leader, then its members in order. */
export function vendorTeamSignature(team: VendorTeam | MaskedTeam | null): string | undefined {
  if (Masked.isMasked(team)) return undefined;
  if (!team) return "";
  const members = team.members.map((member) => member.userId.value).sort();
  return [team.leader?.userId.value ?? "", ...members].join(",");
}

function isArchivedColumn(proposal: Proposal): boolean {
  return proposal.isArchived || proposal.isHired;
}

function comparedTerms(proposal: Proposal, hiredTerms: ProposalHiredTerms | undefined): ComparedTerms {
  if (hiredTerms) {
    return {
      source: "hired",
      hiredAt: hiredTerms.hiredAt,
      vendorTeam: hiredTerms.vendorTeam,
      feeSchedule: hiredTerms.feeSchedule,
    };
  }
  return {
    source: proposal.isHired ? "liveAfterHire" : "live",
    hiredAt: proposal.hireDate,
    vendorTeam: proposal.vendorTeam,
    feeSchedule: proposal.feeSchedule,
  };
}

function categoryKey(name: string): string {
  return name.trim().toLocaleLowerCase();
}

function emptyCell(column: ProposalComparisonColumn): FeeComparisonCell {
  if (column.kind === "unavailable") return { kind: "unavailable" };
  if (Masked.isMasked(column.terms.feeSchedule)) return { kind: "masked" };
  return { kind: "absent" };
}

function comparedFee(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): ComparedFee {
  return {
    name: category.name.value.trim(),
    rate: comparedRate(category),
    teamMember: comparedTeamMember(category),
    type: category.type,
  };
}

function comparedRate(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): ComparedRate {
  if (!FeeScheduleCategoryMaskedCurrency.isNotMasked(category)) return { kind: "currencyMasked" };
  return category.fee ? { kind: "fee", fee: category.fee } : { kind: "deferred" };
}

function comparedTeamMember(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): ComparedTeamMember {
  if (category.teamMemberIsMasked) return { kind: "masked" };
  return category.teamMember ? { kind: "member", member: category.teamMember } : { kind: "none" };
}

/** The attributes two fees are compared by. Each answers undefined when it is withheld from this reader. */
const feeAttributes: readonly ((fee: ComparedFee) => string | undefined)[] = [
  (fee) => categoryKey(fee.name),
  (fee) => {
    if (fee.rate.kind === "currencyMasked") return undefined;
    if (fee.rate.kind === "deferred") return "deferred";
    return `${fee.rate.fee.rate.amount} ${fee.rate.fee.rate.currency.toString()} ${fee.rate.fee.unit ?? "fixed"}`;
  },
  (fee) => {
    if (fee.teamMember.kind === "masked") return undefined;
    return fee.teamMember.kind === "member" ? fee.teamMember.member.userId.value : "";
  },
  (fee) => fee.type,
];

/**
 * Whether the readable cells of one fee row differ in whether they quote it, or in their fees. Each
 * pair of cells is compared fee by fee, on every attribute both of them let this reader see, so a
 * rate stays with the person it bills and a withheld attribute is no evidence either way.
 */
function feeCellsDiffer(cells: readonly FeeComparisonCell[]): boolean {
  if (valuesDiffer(cells.map(feeCellPresence))) return true;
  const quoted = cells.filter((cell) => cell.kind === "fees");
  return quoted.some((cell, index) =>
    quoted.slice(index + 1).some((other) => {
      const shared = readableAttributes(cell).filter((attribute) => readableAttributes(other).includes(attribute));
      return feeCellSignature(cell, shared) !== feeCellSignature(other, shared);
    }),
  );
}

function feeCellPresence(cell: FeeComparisonCell): string | undefined {
  if (cell.kind === "absent") return "absent";
  if (cell.kind === "fees") return "quoted";
  return undefined;
}

type QuotedCell = Extract<FeeComparisonCell, { kind: "fees" }>;

/** The attributes this reader can see on every fee in a cell. */
function readableAttributes(cell: QuotedCell): (typeof feeAttributes)[number][] {
  return feeAttributes.filter((attribute) => cell.fees.every((fee) => attribute(fee) !== undefined));
}

/** A cell's fees as a comparable value: each fee's given attributes together, in a stable order. */
function feeCellSignature(cell: QuotedCell, attributes: readonly (typeof feeAttributes)[number][]): string {
  return cell.fees
    .map((fee) => attributes.map((attribute) => attribute(fee)).join("|"))
    .sort()
    .join(";");
}

function valuesDiffer(signatures: readonly (string | undefined)[]): boolean {
  return new Set(signatures.filter((signature) => signature !== undefined)).size > 1;
}
